import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import {
  getAnimationAppearDisappear,
  getAnimationAppearDisappearWithHeight,
  getAnimationSlideInToLeft,
  getAnimationSlideLeftToRight,
} from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { DebuggingContextService } from '@examdojo/core/debugging';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { provideFaIcons } from '@examdojo/icons';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { QuestionGradingPreset } from '@examdojo/models/question';

import { DraggablePaneComponent, DraggablePaneOptions, DraggablePaneRect } from '@examdojo/core/draggable-pane';
import { PlatformService } from '@examdojo/platform';
import {
  convertQuestionStemMarkschemeUIModelToGradedMarkscheme,
  getContentJsonPathFromCoordinates,
  GradingErrorCode,
  isMultipleChoiceQuestion,
  isSelfGradableGradingError,
  McqSelectionService,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityStoreModel,
  QuestionAttemptSelfGradingService,
  QuestionAttemptStoreModel,
  QuestionFlagFeedbackDirective,
  QuestionGradingPayload,
} from '@examdojo/question';
import { SolutionCaptureService } from '@examdojo/solution';
import { DifficultyComponent } from '@examdojo/ui/difficulty';
import { ImageComponent } from '@examdojo/ui/image';
import { LoaderComponent } from '@examdojo/ui/loader';
import { UserQuery } from '@examdojo/user';
import { faLightbulb } from '@fortawesome/pro-light-svg-icons';
import { faBookmark, faLightbulbOn } from '@fortawesome/pro-solid-svg-icons';
import {
  IonButton,
  IonContent,
  IonFab,
  IonHeader,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { AngularSplitModule } from 'angular-split';
import isEqual from 'lodash/isEqual';
import {
  catchError,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  EMPTY,
  filter,
  finalize,
  first,
  from,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import {
  CHAT_DIALOG_ID,
  ChatContext,
  ChatDialogComponent,
  ChatDialogService,
  ChatMessageQuery,
  ChatMessageService,
  EngagementPhase,
} from '../chat';
import { TopicPracticeV2Service } from '../features/practice/topic-practice/topic-practice-v2.service';
import { FooterPanelComponent } from '../shared/footer-panel/footer-panel.component';
import { FullscreenFlowService } from '../shared/fullscreen-flow';
import { CalculatorIndicatorComponent } from './calculator-indicator/calculator-indicator.component';
import { ElementPosition } from './layout/question-layout.model';
import { QuestionLayoutService } from './layout/question-layout.service';
import { QuestionActionDialogService } from './question-action-dialog/question-action-dialog.service';
import { QuestionGradingResultHeaderComponent } from './question-grading-result/question-grading-header.component';
import { QuestionItemsViewerOldComponent } from './question-items-viewer-old/question-items-viewer-old.component';
import { QuestionItemsViewerComponent } from './question-items-viewer/question-items-viewer.component';
import { QuestionProgressComponent } from './question-progress/question-progress.component';
import { SenseiFabComponent } from './sensei-fab/sensei-fab.component';
import { SolutionSidebarComponent } from './solution-sidebar/solution-sidebar.component';

const MOBILE_BREAKPOINT = 768;

@Component({
  selector: 'dojo-question-old',
  imports: [
    IonContent,
    TranslocoPipe,
    ButtonComponent,
    IonButton,
    CalculatorIndicatorComponent,
    DifficultyComponent,
    IconComponent,
    QuestionFlagFeedbackDirective,
    AngularSplitModule,
    ChatDialogComponent,
    NgTemplateOutlet,
    MatTooltip,
    FooterPanelComponent,
    QuestionGradingResultHeaderComponent,
    IonHeader,
    IconButtonComponent,
    IonTitle,
    IonToolbar,
    SolutionSidebarComponent,
    QuestionItemsViewerOldComponent,
    QuestionItemsViewerComponent,
    QuestionProgressComponent,
    SenseiFabComponent,
    IonFab,
    LoaderComponent,
    NgClass,
    ImageComponent,
    DraggablePaneComponent,
  ],
  providers: [
    ChatDialogService,
    QuestionActionDialogService,
    // temporary as the sidebar has started requiring it.
    QuestionLayoutService,
  ],
  templateUrl: './question-old.component.html',
  styleUrl: './question-old.component.scss',
  host: { class: 'ion-page' },
  animations: [
    getAnimationAppearDisappearWithHeight(),
    getAnimationAppearDisappear(),
    getAnimationSlideInToLeft(),
    getAnimationSlideLeftToRight(),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionOldComponent implements OnDestroy {
  constructor(
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly solutionCaptureService: SolutionCaptureService,
    private readonly destroyRef: DestroyRef,
    private readonly chatDialogService: ChatDialogService,
    private readonly platformService: PlatformService,
    private readonly chatMessageService: ChatMessageService,
    private readonly chatMessageQuery: ChatMessageQuery,
    private readonly modalCtrl: ModalController,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly fullscreenFlowService: FullscreenFlowService,
    private readonly assessmentActionDialogService: QuestionActionDialogService,
    private readonly userQuery: UserQuery,
    private readonly questionAttemptSelfGradingService: QuestionAttemptSelfGradingService,
    private readonly mcqSelectionService: McqSelectionService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly topicPracticeService: TopicPracticeV2Service,
  ) {
    merge(
      this.updateSolutionCaptureContextOnChange(),
      this.reactToResize(),
      this.shouldOpenSolutionCaptureDialogOnDialogFlag(),
      this.debuggingContextService.syncDebuggingContext('Question', this.question$),
      this.debuggingContextService.syncDebuggingContext('Attempt', this.questionAttemptQuery.attempt$),
      this.setChatEngagementPhaseOnAttemptStatusChange(),
      this.setInitialMCQChatContext(),
      this.syncActiveChatContextWithChatMessageQuery(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();

    provideFaIcons([faBookmark, faLightbulb, faLightbulbOn]);
  }

  readonly ElementPosition = ElementPosition;
  readonly activeChatContext = signal<ChatContext | null>(null);
  readonly isDesktopChatVisible = signal(false);
  readonly chatPosition = signal<ElementPosition>(ElementPosition.Right);
  readonly toolbarButtonsTemplate = viewChild<TemplateRef<void>>('toolbarButtonsTemplate');
  readonly splitDirection = computed(() => {
    const position = this.chatPosition();
    return position === ElementPosition.Bottom || position === ElementPosition.Top ? 'vertical' : 'horizontal';
  });

  readonly draggablePaneOptions = signal<DraggablePaneOptions>({
    position: { x: 100, y: 100 },
    size: { width: 500, height: 600 },
    minWidth: 300,
    minHeight: 250,
  });

  readonly openedGradingResultItemId = signal<number | null>(null);

  readonly completed = output<'continue' | 'stop'>();

  readonly sizeWildcard = '*' as unknown as number;
  readonly DRAG_TARGET = 1;

  readonly MIN_LEFT_NAV_WIDTH_PX = 300;
  readonly MAX_LEFT_NAV_WIDTH_PX = 800;
  readonly INITIAL_LEFT_NAV_WIDTH_PX = 480;
  readonly TABLET_LEFT_NAV_WIDTH_PX = 300;

  private readonly question$ = this.questionAttemptQuery.question$;
  private readonly questionAttempt$ = this.questionAttemptQuery.attempt$;
  private readonly questionItems$ = this.questionAttemptQuery.questionItems$;
  private readonly newQuestionFormatQuestionItems$ = this.questionAttemptQuery.newQuestionFormatQuestionItems$;
  private readonly shouldShowNewQuestionFormat$ = this.questionAttemptQuery.shouldShowNewQuestionFormat$;
  private readonly questionDifficulty$ = this.questionAttemptQuery.questionDifficulty$;
  private readonly isQuestionMultipleChoice$ = this.questionAttemptQuery.isQuestionMultipleChoice$;

  private readonly questionAttemptStatus$ = this.questionAttemptQuery.attemptStatus$;

  readonly mcqChatContext$ = this.questionAttemptQuery.active$.pipe(
    filter(isNotNullish),
    distinctUntilKeyChanged('id'),
    map((questionAttemptEntity) => {
      if (!questionAttemptEntity.question) {
        return null;
      }

      const isQuestionMultipleChoice = isMultipleChoiceQuestion(questionAttemptEntity.question.question_items ?? []);

      if (!isQuestionMultipleChoice) {
        return null;
      }

      const optionsItemIndex = questionAttemptEntity.question.question_items?.findIndex(
        (item) => item.type === 'options',
      );
      const coordinates = getContentJsonPathFromCoordinates({ rootItemIndex: optionsItemIndex });

      return { field: 'json_path' as const, id: coordinates };
    }),
    shareOneReplay(),
  );

  private readonly showAwardedMarksBlock$ = combineLatest([
    this.isQuestionMultipleChoice$,
    this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks$,
    this.questionAttemptQuery.questionGradingTotalPoints$,
    this.questionAttemptQuery.questionGradingAwardedPoints$,
    this.questionAttemptQuery.timeSpent$,
    this.questionAttemptQuery.selfGradingAllowed$,
  ]).pipe(
    map(
      ([
        isQuestionMultipleChoice,
        isMultipleChoiceQuestionGradedWithAwardedMarks,
        totalMarks,
        awardedMarks,
        timeSpent,
        selfGradingAllowed,
      ]) =>
        isQuestionMultipleChoice
          ? isMultipleChoiceQuestionGradedWithAwardedMarks
          : selfGradingAllowed
            ? isNotNullish(totalMarks) && isNotNullish(awardedMarks)
            : isNotNullish(totalMarks) && isNotNullish(awardedMarks) && isNotNullish(timeSpent),
    ),
    distinctUntilChanged(),
  );

  private readonly disabledMCQOptions$ = this.questionAttemptQuery.disabledOptionIndexes$.pipe(shareOneReplay());

  readonly isReadyToGrade = computed(() => {
    if (this.solutionCaptureService.isValid()) {
      return true;
    }

    const selectedOptions = this.mcqSelectionService.multipleChoiceOptionSelected();
    const disabledOptions = this.state.disabledMCQOptions;

    if (!selectedOptions || selectedOptions.length === 0) {
      return false;
    }

    // Check if any selected option is NOT in the disabled list
    const hasValidSelection = selectedOptions.some((optionIndex) => !disabledOptions.includes(optionIndex));

    return hasValidSelection;
  });

  private readonly realtimeSelfGradingAwardedMarks$ = combineLatest([
    this.questionAttemptQuery.active$,
    this.questionAttemptQuery.selfGradingAllowed$,
    this.questionAttemptQuery.markCalculationMethod$,
  ]).pipe(
    switchMap(([activeEntity, selfGradingAllowed, markCalculationMethod]) => {
      if (!selfGradingAllowed || !activeEntity?.question?.question_items || !markCalculationMethod) {
        return of(null);
      }

      const allStemCoordinates = this.questionAttemptQuery.getAllStemCoordinates();

      if (allStemCoordinates.length === 0) {
        return of(0);
      }

      const stemMarks$ = allStemCoordinates.map((coordinates) => {
        const stem = this.questionAttemptQuery.getStemByCoordinates(coordinates);

        if (!stem || !stem.markscheme) {
          return of(0);
        }

        const solutionPaths$ = stem.markscheme.solution_paths.map((_, pathIndex: number) => {
          const ctrl = this.questionAttemptSelfGradingService.getSolutionPathControlBySolutionPathIndex(
            coordinates,
            pathIndex,
          );

          return ctrl ? ctrl.valueChanges.pipe(startWith(ctrl.value)) : of([]);
        });

        if (solutionPaths$.length === 0) {
          return of(0);
        }

        return combineLatest(solutionPaths$).pipe(
          map((selectedIdsPerPath: string[][]) =>
            this.questionAttemptQuery.calculateSelfGradingMarksForStem(stem, selectedIdsPerPath, markCalculationMethod),
          ),
        );
      });

      return combineLatest(stemMarks$).pipe(
        map((stemMarks: number[]) => stemMarks.reduce((sum, marks) => sum + marks, 0)),
      );
    }),
    shareOneReplay(),
  );

  private readonly awardedMarksWithRealtimeSelfGrading$ = combineLatest([
    this.questionAttemptQuery.questionGradingAwardedPoints$,
    this.questionAttemptQuery.selfGradingAllowed$,
    this.realtimeSelfGradingAwardedMarks$,
  ]).pipe(
    map(([regularAwardedMarks, selfGradingAllowed, realtimeMarks]) => {
      return selfGradingAllowed && realtimeMarks !== null ? realtimeMarks : regularAwardedMarks;
    }),
  );

  private readonly senseiImageType$: Observable<'neutral' | 'wrong' | 'correct'> = combineLatest([
    this.isQuestionMultipleChoice$,
    this.questionAttemptQuery.mostRecentAttemptGradingAwardedMarksAndStatus$,
  ]).pipe(
    distinctUntilChanged(),
    map(([isMultipleChoice, awardedMarksAndStatus]) => {
      if (
        isMultipleChoice &&
        awardedMarksAndStatus &&
        awardedMarksAndStatus.status === 'GRADED' &&
        typeof awardedMarksAndStatus.awardedMarks === 'number'
      ) {
        if (awardedMarksAndStatus.awardedMarks === 0) {
          return 'wrong';
        } else if (awardedMarksAndStatus.awardedMarks > 0) {
          return 'correct';
        }
      }
      return 'neutral';
    }),
    startWith('neutral' as const),
  );

  readonly state = connectState({
    question: this.question$,
    isQuestionReady: combineLatest([
      this.questionAttemptQuery.questionLoaded$,
      this.topicPracticeService.isTransitioningBetweenQuestions$.pipe(
        switchMap((isTransitioning) => {
          if (isTransitioning) {
            return of(true);
          }

          return of(true).pipe(
            // Preventing some layout shifting
            // Maybe we can do it with just CSS
            delay(500),
            map(() => false),
          );
        }),
        startWith(false),
      ),
    ]).pipe(
      map(([questionLoaded, isTransitioningBetweenQuestions]) => questionLoaded && !isTransitioningBetweenQuestions),
      debounceTime(0),
    ),
    questionFlagged: this.questionAttemptQuery.isQuestionFlagged$,
    questionItems: this.questionItems$,
    newQuestionFormatQuestionItems: this.newQuestionFormatQuestionItems$,
    totalMarks: this.questionAttemptQuery.questionGradingTotalPoints$,
    isMultipleChoiceQuestionGradedWithAwardedMarks:
      this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks$,
    disableMcqCheckAnswerButton: combineLatest([
      this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks$,
      toObservable(this.isReadyToGrade),
    ]).pipe(
      map(([isMultipleChoiceQuestionGradedWithAwardedMarks, isReadyToGrade]) =>
        isMultipleChoiceQuestionGradedWithAwardedMarks ? false : !isReadyToGrade,
      ),
      startWith(false),
      distinctUntilChanged(),
    ),
    awardedMarks: this.awardedMarksWithRealtimeSelfGrading$,
    isQuestionMultipleChoice: this.isQuestionMultipleChoice$,
    showAwardedMarksBlock: this.showAwardedMarksBlock$,
    questionIsInSubmittedStatus: this.questionAttemptStatus$.pipe(map((status) => status === 'SUBMITTED')),
    questionGradedOrFailed: this.questionAttemptStatus$.pipe(
      map((status) => status === 'GRADED' || status === 'FAILED'),
    ),
    attempt: this.questionAttemptQuery.attempt$,
    attemptStatus: this.questionAttemptQuery.attemptStatus$,
    gradingError: this.questionAttemptQuery.gradingError$,
    selfGradingAllowed: this.questionAttemptQuery.selfGradingAllowed$,
    buttonText: this.questionAttemptQuery.buttonText$,
    shouldShowNewQuestionFormat: this.shouldShowNewQuestionFormat$,
    isMobile: this.platformService.isSmall$,
    isTablet: this.platformService.isMedium$,
    mcqChatContext: this.mcqChatContext$,
    formulaBooklet: this.syllabusQuery.activeFormularBooklet$,
    disabledMCQOptions: this.disabledMCQOptions$,
    difficulty: this.questionDifficulty$,
    hasPreviousAttempts: this.questionAttemptQuery.hasPreviousAttempts$,
    markCalculationMethod: this.questionAttemptQuery.markCalculationMethod$,
    showMcqChatSuggestions: this.mcqChatContext$.pipe(
      switchMap((chatContext) => (chatContext ? this.chatMessageQuery.selectMessagesForContext(chatContext) : of([]))),
      map((messages) => !messages.length),
      startWith(false),
      debounceTime(0),
      distinctUntilChanged(),
    ),
    isTestingFlagOn: this.featureFlagService.select(ExamDojoFeatureFlag.Testing),
    isFetchingMCQNextQuestionAttempt: this.questionAttemptQuery.isActiveAttemptShouldBeRecreated$,
    senseiImageType: this.senseiImageType$,
    isMultiQuestionActivity: this.topicPracticeService.isMultiQuestionActivity$,
    mcqButtonText: combineLatest([
      this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks$,
      this.topicPracticeService.isMultiQuestionActivity$,
      this.topicPracticeService.areAllQuestionsCompleted$,
    ]).pipe(
      map(([isMultipleChoiceQuestionGradedWithAwardedMarks, isMultiQuestionActivity, areAllQuestionsCompleted]) => {
        if (isMultiQuestionActivity && areAllQuestionsCompleted) {
          return 'finish';
        }
        return isMultipleChoiceQuestionGradedWithAwardedMarks
          ? 'examdojo.question.next_question'
          : 'examdojo.question.check_answer';
      }),
      distinctUntilChanged(),
    ),
  });

  readonly GradingErrorCode = GradingErrorCode;

  onChatRectChange(newRect: DraggablePaneRect) {
    this.draggablePaneOptions.set({
      ...this.draggablePaneOptions(),
      ...newRect,
    });
  }

  ngOnDestroy() {
    this.chatMessageService.resetChat();
  }

  openSolutionCaptureDialog() {
    if (this.solutionCaptureService.isDialogOpen()) {
      return;
    }

    const previousAttemptStatus = this.state.attempt?.status;
    assertNonNullable(previousAttemptStatus, 'previousAttemptStatus');
    from(this.solutionCaptureService.openDialog())
      .pipe(
        tap((data) => {
          if (!data) {
            return;
          }
          return this.submitForGrading();
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.solutionCaptureService.dismiss();
        }),
      )
      .subscribe();
  }

  multipleChoiceQuestionGrading() {
    const isQuestionMultipleChoiceGraded = this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks();

    if (isQuestionMultipleChoiceGraded) {
      return this.goToNextQuestion();
    }

    let preset = this.state.isTestingFlagOn ? this.solutionCaptureService.selectedPreset() : undefined;
    if (preset && preset === QuestionGradingPreset.GradingError) {
      preset = undefined;
    }

    if (isNotNullish(preset)) {
      return this.submitForGrading();
    }

    const context = this.solutionCaptureService.context();
    assertNonNullable(context, 'context');

    const selectedChoices = this.mcqSelectionService.multipleChoiceOptionSelected();

    if (this.mcqSelectionService.hasSomeChoiceOptionSelected()) {
      return this.questionAttemptService
        .submitForGradingOptionsQuestion(this.mcqSelectionService.multipleChoiceOptionSelected(), context.attemptId)
        .pipe(
          catchError(() => {
            this.fullscreenFlowService.dismiss();

            return EMPTY;
          }),
          switchMap(() => this.questionAttemptQuery.active$.pipe(first((entity) => !!entity && !!entity.chat))),
          finalize(() => {
            this.mcqSelectionService.resetMultipleChoiceOptionSelected(selectedChoices);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  submitForGrading() {
    const preset = this.state.isTestingFlagOn ? this.solutionCaptureService.selectedPreset() : undefined;
    const context = this.solutionCaptureService.context();
    assertNonNullable(context, 'context');

    return this.questionAttemptService
      .submitForGrading(context.attemptId, preset)
      .pipe(
        catchError((err: unknown) => {
          if (!isSelfGradableGradingError(err) && !preset) {
            this.fullscreenFlowService.dismiss();
          }
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  selfGradeQuestionAttempt() {
    const attemptId = this.questionAttemptQuery.getActiveId();

    assertNonNullable(attemptId, 'engagementId');

    let gradingResult: QuestionGradingPayload | null = null;

    if (this.state.shouldShowNewQuestionFormat) {
      gradingResult = this.questionAttemptSelfGradingService.populateQuestionItemsWithSelfGradingResult(
        this.state.newQuestionFormatQuestionItems,
      );
    } else {
      const gradableMarkscheme =
        this.state.questionItems
          ?.map((item) => {
            if (item.type !== 'stem') {
              return null;
            }

            return item;
          })
          .filter(isNotNullish) ?? [];

      gradingResult = convertQuestionStemMarkschemeUIModelToGradedMarkscheme(gradableMarkscheme);
    }

    if (!gradingResult) {
      return;
    }

    this.questionAttemptService
      .submitForSelfGrading(gradingResult, attemptId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  setChatClosed() {
    this.activeChatContext.set(null);
    this.isDesktopChatVisible.set(false);
  }

  onElementPositionChanged(position: ElementPosition) {
    this.chatPosition.set(position);
  }

  toggleChatDialog(chatContext: ChatContext) {
    if (this.activeChatContext()?.id === chatContext.id && this.isDesktopChatVisible() && !this.state.isMobile) {
      this.setChatClosed();
    } else {
      this.activeChatContext.set(chatContext);
      if (this.platformService.width >= MOBILE_BREAKPOINT) {
        this.isDesktopChatVisible.set(true);
      }
      this.openChatDialog(chatContext);
    }
  }

  toggleGradingResult(itemId: number) {
    if (this.openedGradingResultItemId() === itemId) {
      this.openedGradingResultItemId.set(null);
    } else {
      this.openedGradingResultItemId.set(itemId);
    }
  }

  goToNextQuestion() {
    this.isDesktopChatVisible.set(false);
    this.completed.emit('continue');
  }

  endPractice() {
    this.completed.emit('stop');
  }

  openChatDialog(chatContext: ChatContext) {
    this.activeChatContext.set(chatContext);
    this.updateChatContext(chatContext);

    if (this.state.isMobile) {
      this.chatDialogService.openDialog().then(() => {
        this.activeChatContext.set(null);
      });
    } else {
      this.isDesktopChatVisible.set(true);
    }
  }

  handleClickedFab(suggestionValue: string | undefined) {
    const chatContext = this.state.mcqChatContext;
    if (!chatContext) {
      return;
    }

    this.toggleChatDialog(chatContext);

    if (suggestionValue) {
      this.chatMessageService
        .addUserMessage(chatContext, { text: suggestionValue, images: [] })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  openQuestionActionDialog() {
    from(this.assessmentActionDialogService.openDialog()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private updateSolutionCaptureContextOnChange() {
    return combineLatest([
      this.userQuery.selectActiveId(),
      this.questionAttempt$.pipe(
        filter(Boolean),
        map((attempt) => attempt?.id),
      ),
    ]).pipe(
      distinctUntilChanged(isEqual),
      tap(([userId, attemptId]) => this.solutionCaptureService.setContext({ userId, attemptId })),
    );
  }

  private updateChatContext(
    chatContext: ChatContext,
    entity: QuestionAttemptEntityStoreModel | null = this.questionAttemptQuery.getActive(),
  ) {
    const questionAttemptChat = entity?.chat;

    const status = this.questionAttemptQuery.isQuestionMultipleChoice()
      ? this.questionAttemptQuery.getMostRecentAttemptGradingAwardedMarksAndStatus()?.status
      : entity?.attempt.status;

    const phase = this.getEngagementPhaseFromAttemptStatus(status ?? null);

    this.chatMessageService.setCurrentChatContext({
      chatId:
        phase === EngagementPhase.PreGrading
          ? questionAttemptChat?.pre_grading_llm_chat_id ?? null
          : questionAttemptChat?.post_grading_llm_chat_id ?? null,
      chatContext,
    });
  }

  private shouldOpenSolutionCaptureDialogOnDialogFlag() {
    return combineLatest([this.questionAttemptQuery.openCaptureDialog$, this.question$]).pipe(
      filter(([openCaptureDialog, question]) => !!openCaptureDialog && !!question),
      tap(([openCaptureDialogFlag]) => {
        if (openCaptureDialogFlag) {
          this.openSolutionCaptureDialog();
          this.questionAttemptService.setOpenCaptureDialogFlag(false);
        }
      }),
    );
  }

  private reactToResize() {
    return this.platformService.resize$.pipe(
      debounceTime(150),
      tap(({ width }) => {
        if (width < MOBILE_BREAKPOINT && !this.state.isMobile) {
          this.setChatClosed();
        } else if (width >= MOBILE_BREAKPOINT && this.state.isTablet) {
          this.modalCtrl.dismiss(null, '', CHAT_DIALOG_ID);
        }
      }),
    );
  }

  private setChatEngagementPhaseOnAttemptStatusChange() {
    return this.isQuestionMultipleChoice$.pipe(
      switchMap((isQuestionMultipleChoice) => {
        if (isQuestionMultipleChoice) {
          return this.questionAttemptQuery.mostRecentAttemptGradingAwardedMarksAndStatus$.pipe(
            map((marksAndStatus) => {
              return (marksAndStatus && marksAndStatus.status) ?? null;
            }),
          );
        }

        return this.questionAttemptQuery.attemptStatus$;
      }),
      map((status) => this.getEngagementPhaseFromAttemptStatus(status ?? null)),
      distinctUntilChanged(),
      tap((phase) => {
        this.chatMessageService.setEngagementPhase(phase);
      }),
      tap((phase) => {
        if (phase === EngagementPhase.PostGrading) {
          this.setChatClosed();
        }
      }),
      takeWhile((phase) => phase !== EngagementPhase.PostGrading, true),
    );
  }

  private setInitialMCQChatContext() {
    return this.mcqChatContext$.pipe(
      filter(isNotNullish),
      tap((chatContext) => {
        this.updateChatContext(chatContext);
      }),
    );
  }

  private syncActiveChatContextWithChatMessageQuery() {
    return this.chatMessageQuery.selectProp('chatContext').pipe(
      filter((chatContext) => chatContext !== null),
      tap((chatContext) => {
        // Only update if the chat dialog is visible (either mobile or desktop)
        if (this.isDesktopChatVisible() || this.state.isMobile) {
          this.activeChatContext.set(chatContext);
        }
      }),
    );
  }

  private getEngagementPhaseFromAttemptStatus(status: QuestionAttemptStoreModel['status'] | null): EngagementPhase {
    return status === 'GRADED' ? EngagementPhase.PostGrading : EngagementPhase.PreGrading;
  }
}
